import { useState, useEffect, useRef, useCallback } from "react";
import { VideoOff, Play, Pause, Volume2, VolumeX } from "lucide-react";
import { useAuthenticatedVideoUrl } from "@/hooks/useAuthenticatedMedia";
import { cn } from "@/lib/utils";

interface AuthenticatedVideoProps extends Omit<
  React.VideoHTMLAttributes<HTMLVideoElement>,
  "src" | "poster"
> {
  src: string | undefined | null;
  socialControls?: boolean;
  /**
   * Optional first-frame thumbnail shown before the video enters the
   * viewport. Once the card scrolls into view, the real <video> element
   * mounts and takes over.
   */
  poster?: string;
}

// Intersection thresholds:
//  - LOAD: at >=20% visible we resolve+attach the real src and ask the
//    browser for the metadata bytes (enough to render the first frame).
//  - PLAY: at >=60% visible the card is considered a candidate for
//    autoplay. The module-level coordinator below then picks ONE
//    candidate (highest intersection ratio) and pauses the rest.
const LOAD_THRESHOLD = 0.2;
const PLAY_THRESHOLD = 0.6;

// ---------------------------------------------------------------------------
// Cross-instance autoplay coordinator.
//
// Requirement (Task #1730): "autoplay only most-visible card" — multiple
// AuthenticatedVideo instances may simultaneously satisfy the PLAY_THRESHOLD
// (e.g. on tall viewports or short cards). Without arbitration, every one of
// them would call .play() and the user would hear several audio tracks
// overlap and pay decode cost on all of them.
//
// Strategy: each mounted+autoplay-enabled video registers itself with its
// current intersection ratio + play/pause callbacks. After every update we
// pick the single candidate with the highest ratio (above PLAY_THRESHOLD)
// and call play() on it, pause() on every other registered video. The
// coordinator is intentionally a plain module singleton — it has no React
// dependencies and survives route changes within the SPA.
// ---------------------------------------------------------------------------
interface VideoCandidate {
  ratio: number;
  play: () => void;
  pause: () => void;
}
const videoRegistry = new Set<VideoCandidate>();
let activeCandidate: VideoCandidate | null = null;

function reconcileActiveVideo() {
  const candidates = Array.from(videoRegistry).filter(
    (c) => c.ratio >= PLAY_THRESHOLD
  );
  candidates.sort((a, b) => b.ratio - a.ratio);
  const best: VideoCandidate | null = candidates[0] ?? null;
  if (activeCandidate && activeCandidate !== best) {
    activeCandidate.pause();
  }
  activeCandidate = best;
  if (best) best.play();
}

function registerVideo(c: VideoCandidate) {
  videoRegistry.add(c);
  reconcileActiveVideo();
}

function unregisterVideo(c: VideoCandidate) {
  videoRegistry.delete(c);
  if (activeCandidate === c) activeCandidate = null;
  reconcileActiveVideo();
}

export function AuthenticatedVideo({
  src,
  className,
  // Extract autoPlay and muted so they are NOT spread onto the DOM element.
  // We handle both solely via the DOM property + programmatic play() call,
  // which is the only reliable approach on iOS Safari.
  autoPlay,

  muted: _muted,
  // Extract socialControls so it is never spread onto the DOM video element.
  socialControls = false,
  // Extract `controls` so we can intentionally suppress it in socialControls
  // mode — native controls must never appear over our custom overlay.
  controls,
  poster,
  ...props
}: AuthenticatedVideoProps) {
  const resolvedSrc = useAuthenticatedVideoUrl(src ?? undefined);
  const [errored, setErrored] = useState(false);
  // Initialise isMuted to true (matches autoplay-muted policy). We sync from
  // the actual DOM element on canplay so the icon always reflects true state.
  const [isMuted, setIsMuted] = useState(true);
  const [flashIcon, setFlashIcon] = useState<"play" | "pause" | null>(null);
  const [flashFading, setFlashFading] = useState(false);
  // Has the card crossed the LOAD threshold at least once? We never go back
  // to "unloaded" — once we've started buffering metadata we keep the src
  // attached so re-scrolling into view is instant. (Spec allows clearing src
  // on low-end devices; we opt for the simpler/snappier UX.)
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Stable per-instance candidate object used to register with the
  // module-level autoplay coordinator. The coordinator updates
  // `candidateRef.current.ratio` from IntersectionObserver callbacks and
  // calls play() / pause() to enforce the "only most-visible plays" rule.
  const candidateRef = useRef<VideoCandidate | null>(null);
  const flashShowTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup flash timers on unmount
  useEffect(() => {
    return () => {
      if (flashShowTimer.current) clearTimeout(flashShowTimer.current);
      if (flashHideTimer.current) clearTimeout(flashHideTimer.current);
    };
  }, []);

  // Reset error state when source changes
  useEffect(() => {
    setErrored(false);
  }, [resolvedSrc]);

  // Viewport-gated loading + autoplay registration.
  //
  // The IntersectionObserver is the single source of truth for two things:
  //   1. shouldLoad: once intersectionRatio >= LOAD_THRESHOLD we attach the
  //      real src and let the browser fetch metadata. One-way latch.
  //   2. candidateRef.ratio: the current visibility for the autoplay
  //      coordinator. After each update we call reconcileActiveVideo() so
  //      the coordinator can pick the single highest-ratio candidate.
  //
  // Only autoplay-enabled videos are registered with the coordinator —
  // non-autoplay videos (e.g. partner dashboard previews) must never be
  // auto-started and must never displace another video's playback.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const candidate: VideoCandidate = {
      ratio: 0,
      play: () => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = true;
        setIsMuted(true);
        video.play().catch(() => {
          // Autoplay blocked by browser policy — not a fatal error
        });
      },
      pause: () => {
        const video = videoRef.current;
        if (video && !video.paused) video.pause();
      },
    };
    candidateRef.current = candidate;
    if (autoPlay) registerVideo(candidate);

    if (typeof IntersectionObserver === "undefined") {
      // Old browsers / SSR — fall back to eager behavior: load
      // immediately, and let the coordinator pick the first registered
      // candidate (still arbitrated globally).
      setShouldLoad(true);
      candidate.ratio = 1;
      if (autoPlay) reconcileActiveVideo();
      return () => {
        if (autoPlay) unregisterVideo(candidate);
        candidateRef.current = null;
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.intersectionRatio >= LOAD_THRESHOLD) {
          setShouldLoad(true);
        }
        candidate.ratio = entry.intersectionRatio;
        if (autoPlay) reconcileActiveVideo();
      },
      { threshold: [LOAD_THRESHOLD, PLAY_THRESHOLD] }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (autoPlay) unregisterVideo(candidate);
      candidateRef.current = null;
    };
  }, [autoPlay]);

  // iOS Safari requires muted to be set as a DOM *property* (not just an HTML attribute)
  // and autoPlay must be triggered via .play() programmatically for reliable playback.
  // canPlay fires once metadata is ready; we re-run the coordinator so the
  // newly-ready video can be selected as the active candidate (the first
  // play() attempt before metadata loaded would have been a no-op).
  const handleCanPlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setIsMuted(video.muted);
    if (autoPlay) reconcileActiveVideo();
  }, [autoPlay]);

  // Toggle play/pause and flash the center icon for ~0.8 s
  const handleTap = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const willPlay = video.paused;
    if (willPlay) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }

    // Reset any existing flash timers
    if (flashShowTimer.current) clearTimeout(flashShowTimer.current);
    if (flashHideTimer.current) clearTimeout(flashHideTimer.current);

    // Show icon at full opacity
    setFlashIcon(willPlay ? "play" : "pause");
    setFlashFading(false);

    // Start fade-out after 400 ms
    flashShowTimer.current = setTimeout(() => setFlashFading(true), 400);

    // Unmount icon after full 800 ms (400 visible + 400 fade)
    flashHideTimer.current = setTimeout(() => {
      setFlashIcon(null);
      setFlashFading(false);
    }, 800);
  }, []);

  const handleMuteToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Must not trigger play/pause tap handler
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  }, []);

  if (!resolvedSrc) return null;

  if (errored) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-2 bg-muted text-muted-foreground ${className ?? ""}`}
        data-testid="video-error-fallback"
      >
        <VideoOff className="w-8 h-8 opacity-50" />
        <span className="text-xs text-center px-3">
          Video not supported on this device
        </span>
      </div>
    );
  }

  // Only attach the real src once the card enters the viewport. Before that
  // we render a placeholder (or the supplied poster) inside the same-sized
  // container so layout doesn't jump on hydration.
  const videoSrc = shouldLoad ? resolvedSrc : undefined;
  const videoPreload = shouldLoad ? "metadata" : "none";

  if (socialControls) {
    return (
      <div
        ref={containerRef}
        className={cn("relative cursor-pointer select-none", className)}
        onClick={handleTap}
        role="button"
        aria-label="Toggle play/pause"
      >
        {shouldLoad ? (
          <video
            ref={videoRef}
            playsInline
            preload={videoPreload}
            poster={poster}
            {...props}
            className="w-full h-full object-cover"
            src={videoSrc}
            onError={() => setErrored(true)}
            onCanPlay={handleCanPlay}
          />
        ) : (
          <VideoPlaceholder poster={poster} />
        )}

        {/* Center play/pause flash icon */}
        {flashIcon && (
          <div
            className={cn(
              "absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300",
              flashFading ? "opacity-0" : "opacity-100"
            )}
          >
            <div className="bg-black/40 backdrop-blur-sm rounded-full p-4">
              {flashIcon === "play" ? (
                <Play className="w-10 h-10 text-white fill-white" />
              ) : (
                <Pause className="w-10 h-10 text-white fill-white" />
              )}
            </div>
          </div>
        )}

        {/* Persistent mute/unmute button — bottom-right corner */}
        <button
          onClick={handleMuteToggle}
          className="absolute bottom-2 right-2 flex items-center justify-center w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm text-white"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
        >
          {isMuted ? (
            <VolumeX className="w-4 h-4" />
          ) : (
            <Volume2 className="w-4 h-4" />
          )}
        </button>
      </div>
    );
  }

  // Non-social mode: restore `controls` that was extracted from props.
  // We still wrap in a div so the IntersectionObserver has a stable node
  // (the <video> itself becomes the observed node only after `shouldLoad`).
  return (
    <div ref={containerRef} className={className}>
      {shouldLoad ? (
        <video
          ref={videoRef}
          playsInline
          preload={videoPreload}
          controls={controls}
          poster={poster}
          {...props}
          className="w-full h-full"
          src={videoSrc}
          onError={() => setErrored(true)}
          onCanPlay={handleCanPlay}
        />
      ) : (
        <VideoPlaceholder poster={poster} />
      )}
    </div>
  );
}

/**
 * Same-sized placeholder rendered before the card enters the viewport.
 * Uses the optional poster image when supplied, otherwise a subtle
 * shimmer so the card never appears blank.
 */
function VideoPlaceholder({ poster }: { poster?: string }) {
  if (poster) {
    return (
      <img
        src={poster}
        alt=""
        className="w-full h-full object-cover"
        loading="lazy"
        decoding="async"
      />
    );
  }
  return (
    <div className="w-full h-full bg-muted animate-pulse" aria-hidden="true" />
  );
}
